import { useState } from 'react'
import { EstadoError } from '../../componentes/EstadoError'
import { useResolver } from './consultas'
import type { FilaAgendamiento } from './filas'
import { filasDeLista } from './filas'
import { aEntradas, analizarLista } from './lista'
import { PlantillaExcel } from './PlantillaExcel'
import { SubirExcel } from './SubirExcel'

/**
 * El modo masivo: pegar la lista de un dia, revisarla y publicarla entera. Es
 * lo que hace hoy la herramienta `Agendamiento masivo admin` de n8n.
 *
 * La fecha **no va por linea**: se elige una vez arriba, para toda la lista. Es
 * como se reparte, y ponerla por linea invitaria a mezclar dias en una misma
 * revision.
 *
 * Lo que no se entiende se marca y sigue: al backend solo viajan las lineas
 * legibles, y las demas vuelven a su sitio en la tabla con su motivo.
 */

const EJEMPLO = '20468583D\t4\t18.9\tdispensador\nB12899720\t3\t12.8'

interface Props {
  onFilas: (filas: FilaAgendamiento[]) => void
}

export function ModoMasivo({ onFilas }: Props) {
  const [texto, setTexto] = useState('')
  const resolver = useResolver()

  const lineas = analizarLista(texto)
  const entradas = aEntradas(lineas)

  const resolverLista = () => {
    if (lineas.length === 0) return
    resolver.mutate(
      { clientes: entradas },
      {
        onSuccess: (respuesta) => onFilas(filasDeLista(lineas, respuesta.resultados)),
      },
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <label htmlFor="lista-masiva" className="block text-sm font-medium text-tinta-2">
          Lista de clientes
        </label>
        <p className="mt-1 text-sm text-tinta-apagada">
          Una linea por cliente: NIF, bono, litraje y productos. El bono es opcional; sin el, la
          linea es <span className="whitespace-nowrap">NIF + productos</span>, separados con{' '}
          <span className="whitespace-nowrap">+</span> y con la cantidad detras (
          <span className="whitespace-nowrap">OROTANA 18.9L x 2</span>). Las columnas se separan con
          tabulador, punto y coma, coma o espacios, para que valga pegar desde Excel.
        </p>
        <div className="mt-2 flex flex-col gap-2">
          <PlantillaExcel />
          <SubirExcel onTexto={setTexto} />
        </div>
        <textarea
          id="lista-masiva"
          rows={8}
          spellCheck={false}
          value={texto}
          onChange={(evento) => setTexto(evento.target.value)}
          placeholder={EJEMPLO}
          className="mt-2 w-full rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 font-mono text-sm text-tinta outline-none focus:border-foco focus:ring-2 focus:ring-foco-halo"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={resolverLista}
          disabled={lineas.length === 0 || resolver.isPending}
          className="rounded-lg border border-acento-borde bg-acento-suave px-4 py-2 text-sm font-semibold text-acento-tinta transition-colors hover:bg-acento-tinta-suave disabled:cursor-not-allowed disabled:border-borde disabled:bg-desactivado disabled:text-desactivado-tinta"
        >
          {resolver.isPending ? 'Resolviendo la lista' : 'Resolver la lista'}
        </button>
        {lineas.length > 0 ? (
          <p className="text-sm text-tinta-apagada">
            {lineas.length === 1 ? '1 linea leida' : `${lineas.length} lineas leidas`}
            {entradas.length === lineas.length
              ? ''
              : `, ${lineas.length - entradas.length} sin entender`}
            .
          </p>
        ) : null}
      </div>

      {resolver.isError ? <EstadoError error={resolver.error} /> : null}
    </div>
  )
}
